import appConfig from '@/config/appConfig.typed';
import {AEM_PLATFORM_NAMES} from '@/consts/aem/aemPlatformNames';
import userBrowserService from '@/services/user/userBrowserService';
import uaUtils from '@/utils/uaUtils';

const getDeviceData = (type) => {
    const deviceSupport = appConfig.getDeviceSupport();

    return deviceSupport.find((x) => x.deviceType === type);
};

const isDeviceSupported = (type) => {
    return !!getDeviceData(type);
};

const deviceOrder = (type) => {
    return getDeviceData(type)?.order;
};

const isInstallPwaEnabled = () => {
    const currentGetStartedPage = getGetStartedPage();

    return !!currentGetStartedPage && !userBrowserService.isInStandaloneMode();
};

const getGetStartedPage = () => {
    const getStartedPages = appConfig.getGetStartedPage();
    const result = findPageByPlatform(getStartedPages, true);

    return result;
};

const matchesPlatformName = (item, platformName) => {
    return item.page?.platform?.name?.toLowerCase() === platformName?.toLowerCase();
};

const findItemsForPlatform = (items, platformName) => {
    if (!platformName) return [];

    return items.filter((item) => matchesPlatformName(item, platformName));
};

const findFirstMatchingPlatform = (items, platformNames) => {
    for (const platformName of platformNames) {
        const matchedItems = findItemsForPlatform(items, platformName);

        if (matchedItems.length > 0) {
            return matchedItems;
        }
    }

    return null;
};

const parseVersionNumber = (versionString) => {
    if (!versionString) return null;
    const version = parseInt(versionString, 10);

    return isNaN(version) ? null : version;
};

const enrichItemWithVersion = (item) => {
    const platform = item.page?.platform || {};

    return {
        item,
        osVersion: parseVersionNumber(platform.osVersion),
        browserVersion: parseVersionNumber(platform.browserVersion),
    };
};

/**
 * Determines whether the entry item is compatible with the current OS and browser versions.
 *
 * Each entry may specify an OS version, a browser version, or both.
 * - If both values are null, the entry is considered incompatible.
 * - If both are present, compatibility requires that both versions are less than or equal to the current ones.
 * - If only one version is provided, compatibility is determined based solely on it.
 */
const isVersionCompatible = (entry, currentOSVersion, currentBrowserVersion) => {
    const {osVersion, browserVersion} = entry;

    if (osVersion === null && browserVersion === null) return false;

    const osOk = osVersion !== null && osVersion <= currentOSVersion;
    const browserOk = browserVersion !== null && browserVersion <= currentBrowserVersion;

    if (osVersion !== null && browserVersion !== null) {
        return osOk && browserOk;
    }

    return osOk || browserOk;
};

const selectBestVersionMatch = (itemsWithVersions, currentOSVersion, currentBrowserVersion) => {
    const compatibleItems = itemsWithVersions
        .filter((entry) => isVersionCompatible(entry, currentOSVersion, currentBrowserVersion))
        .sort((a, b) => {
            const osDiff = (b.osVersion || 0) - (a.osVersion || 0);
            const browserDiff = (b.browserVersion || 0) - (a.browserVersion || 0);

            return osDiff || browserDiff;
        });

    return compatibleItems[0]?.item || null;
};

const selectFallbackItem = (itemsWithVersions, allItems) => {
    const itemWithoutVersions = itemsWithVersions.find(
        (entry) => entry.osVersion === null && entry.browserVersion === null
    );

    return itemWithoutVersions?.item || allItems[0];
};

const findPageByPlatform = (items, skipDefault = false) => {
    if (items?.length === 0) return null;

    const {currentPlatform, parentPlatform, defaultPlatform} = getPlatformName();

    const platformPriority = [
        currentPlatform,
        parentPlatform,
        ...(skipDefault ? [] : [defaultPlatform, AEM_PLATFORM_NAMES.DEFAULT]),
    ];

    const platformItems = findFirstMatchingPlatform(items, platformPriority);

    if (!platformItems) return null;

    if (platformItems.length === 1) {
        return platformItems[0];
    }

    const itemsWithVersions = platformItems.map(enrichItemWithVersion);
    const currentOSVersion = uaUtils.parseMajorVersion(uaUtils.getOSVersion());
    const currentBrowserVersion = uaUtils.parseMajorVersion(uaUtils.getBrowserVersion());

    const bestVersionMatch = selectBestVersionMatch(itemsWithVersions, currentOSVersion, currentBrowserVersion);

    if (bestVersionMatch) return bestVersionMatch;

    return selectFallbackItem(itemsWithVersions, platformItems);
};

const getPlatformName = () => {
    const isMobile = uaUtils.isMobile();
    const isTablet = userBrowserService.isTabletDevice();
    const isIosTablet = userBrowserService.isIosDevice() && isTablet;

    const returnWithDefault = (currentPlatform) => {
        const defaultPlatform = isMobile
            ? AEM_PLATFORM_NAMES.DEFAULT_MOBILE
            : isTablet
              ? AEM_PLATFORM_NAMES.DEFAULT_TABLET
              : AEM_PLATFORM_NAMES.DEFAULT;

        return {currentPlatform, defaultPlatform};
    };

    if (isMobile && userBrowserService.isBluefyBrowser()) {
        return returnWithDefault(AEM_PLATFORM_NAMES.BLUEFY_NAME);
    }

    if (isMobile && userBrowserService.isIosSafariBrowser()) {
        return returnWithDefault(AEM_PLATFORM_NAMES.IOS_SAFARI_NAME);
    }

    if (isMobile && userBrowserService.isIosChromeBrowser()) {
        return returnWithDefault(AEM_PLATFORM_NAMES.IOS_CHROME_NAME);
    }

    if (isMobile) return returnWithDefault(AEM_PLATFORM_NAMES.DEFAULT_MOBILE);

    if (isIosTablet && userBrowserService.isBluefyBrowser()) {
        return returnWithDefault(AEM_PLATFORM_NAMES.BLUEFY_TABLET_NAME);
    }

    if (isIosTablet && userBrowserService.isSafariBrowser()) {
        return returnWithDefault(AEM_PLATFORM_NAMES.IOS_SAFARI_TABLET_NAME);
    }

    if (isIosTablet && userBrowserService.isChromeBrowser()) {
        return returnWithDefault(AEM_PLATFORM_NAMES.IOS_CHROME_TABLET_NAME);
    }

    if (isIosTablet && userBrowserService.isEdgeBrowser()) {
        return returnWithDefault(AEM_PLATFORM_NAMES.IOS_EDGE_TABLET_NAME);
    }

    if (isIosTablet) return returnWithDefault(AEM_PLATFORM_NAMES.DEFAULT);

    if (isTablet) return returnWithDefault(AEM_PLATFORM_NAMES.DEFAULT_TABLET);

    if (userBrowserService.isChromeBrowser()) {
        return returnWithDefault(AEM_PLATFORM_NAMES.CHROME_NAME);
    }

    if (userBrowserService.isEdgeBrowser()) {
        return returnWithDefault(AEM_PLATFORM_NAMES.EDGE_NAME);
    }

    if (userBrowserService.isSafariBrowser()) {
        return returnWithDefault(AEM_PLATFORM_NAMES.SAFARI_NAME);
    }

    return returnWithDefault(AEM_PLATFORM_NAMES.DEFAULT);
};

export default {
    isDeviceSupported,
    deviceOrder,
    getDeviceData,
    getGetStartedPage,
    isInstallPwaEnabled,
    findPageByPlatform,
};
